import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError } from '@utils/types/shared/base';
import { PUT_URL } from '@utils/api/endpoints';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';

export interface UpdateWebhookPayload {
  type: string;
  webhook_url: string;
  webhook_method: string;
  webhook_headers: string[];
  organization_id?: number;
}

export const useUpdateWebhook = (customerId: number, webhookId: number) => {
  const queryClient = useQueryClient();
  const { searchParams } = useNewAppSearchParams();

  // Mirror the exact params used by useGetWebhooks
  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sortField = (searchParams.sort as string | undefined) || undefined;
  const sortDirection =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;

  const qString = (searchParams.q as string | undefined) || undefined;

  const updateWebhook = async (payload: UpdateWebhookPayload) => {
    const { data } = await Axios.put(
      PUT_URL(customerId.toString(), webhookId.toString()).customerWebhook,
      payload
    );
    return data;
  };

  return useMutation<void, AxiosError<BaseError>, UpdateWebhookPayload>(
    updateWebhook,
    {
      onSuccess: () => {
        // Invalidate the exact list key used by useGetWebhooks
        queryClient.invalidateQueries([
          'customerWebhooks',
          customerId,
          offset,
          limit,
          qString,
          sortField,
          sortDirection
        ]);

        // Also invalidate any other cached variants for this customer just in case
        queryClient.invalidateQueries({
          predicate: q =>
            Array.isArray(q.queryKey) &&
            q.queryKey[0] === 'customerWebhooks' &&
            q.queryKey[1] === customerId
        });
      },
      useErrorBoundary: false
    }
  );
};
